/**
 * Date Night: sealed cards, three glyphs, scratch to commit — and one wall with two faces.
 *
 * The face arrives from the server only after the scratch — there is nothing to peek at in the
 * client, which is why the sealed tile below renders glyphs and a question mark and could not show
 * a title if it wanted to.
 *
 * The screen used to be two places behind a Scrapbook / Card wall button in the header: a drawer of
 * question marks, and, somewhere else, a list of what the drawer had become. They are the same
 * shelf read two ways, so they are now one wall behind a **Sealed | Done** switch, with Done drawn
 * as a mosaic of the photos, 1SE's calendar wall, so a year of dates reads as one picture rather
 * than as a scroll.
 *
 * THE WALL IS A GRID, not a row that wraps. `flex-wrap` with a growing basis stretched whatever
 * happened to land on the last row — two half-width tiles under a three-column wall on a phone,
 * five stretched tiles under six on a desktop — so the tracks are fixed fractions of the row and
 * the last one is as wide as every other one.
 */
import { Empty } from '@/components/empty';
import { FLIGHT_MS } from '@/components/face-down-card';
import { Panel } from '@/components/panel';
import { Segmented } from '@/components/segmented';
import { Chip } from '@/components/chip';
import { WheelPicker } from '@/components/wheel-picker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/api';
import { dayLabel, localDay } from '@/lib/day';
import { useReducedMotion } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useLayout } from '@/useLayout';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { usePoll } from '@/usePoll';
import { useStore, whoLabel } from '@/store';
import * as ImagePicker from 'expo-image-picker';
import * as React from 'react';
import { Animated, Image, Pressable, ScrollView, View } from 'react-native';

interface DateCard {
  id: string;
  status: 'sealed' | 'scratched';
  timeIcon: 'short' | 'evening' | 'day';
  budgetIcon: 'free' | 'low' | 'medium' | 'high';
  prepIcon: 'none' | 'some' | 'plan_ahead';
  title?: string;
  body?: string;
  scratchedBy?: 'A' | 'B';
  /** The local day the two of them said they would do it, or null. Joint; nobody's name on it. */
  plannedFor?: string | null;
}

/**
 * The scrapbook holds two kinds of thing now: dates the two of them did, and wraps they chose to
 * keep. Same shelf on purpose — it is already the place they look back at, and a gallery of its
 * own for one photo a month would be a tab nobody opens.
 *
 * `photo` is NOT on the list. It arrives per entry from `/api/dates/scrapbook/:id`, once, and is
 * held for as long as the panel is open — the list is polled every thirty seconds and a year of
 * photographs on every poll is megabytes a minute to redraw an unchanged wall.
 */
interface ScrapbookEntry {
  type: 'date' | 'wrap';
  id: string;
  title: string;
  body?: string;
  note: string | null;
  hasPhoto?: boolean;
  author: 'A' | 'B';
  completedAt: string;
}

/** Which face of the wall is showing. The scrapbook IS the Done wall — see the mosaic below. */
type WallMode = 'sealed' | 'done';

/**
 * The wall's two faces, as the system's segmented control rather than a pair of pills.
 *
 * `Chip` is the loose-pill sibling — several independent picks sitting in a row. This is the one
 * where the two options are the two halves of a single question, so they are drawn joined inside
 * one track and the unpicked half still reads as part of the control rather than as an unpressed
 * button.
 */
const WALL_OPTIONS: readonly { value: WallMode; label: string }[] = [
  { value: 'sealed', label: 'Sealed' },
  { value: 'done', label: 'Done' },
];

/**
 * THE THREE GLYPHS — flat, monochrome, and out of the app's own set.
 *
 * They were emoji: ⏱ 🌆 🌞 🆓 💵 💳 💎 ⚡ 🧰 📅. An emoji is a colour image the theme cannot touch,
 * so at night thirty sealed tiles carried the only saturated non-red pixels on the page, at full
 * light-mode saturation, on a wall whose whole job is to be a quiet grid of question marks.
 *
 * Three families, each a FILL PROGRESSION, so the three positions are readable as "more" without
 * anybody being told which is which: a filling circle for how long, a filling diamond for how much,
 * a filling square for how much setting-up. `text-muted-foreground` in both looks and both modes,
 * because these are the shape of the evening and not its decoration.
 */
const TIME = { short: { glyph: '◔', label: 'an hour-ish' }, evening: { glyph: '◑', label: 'an evening' }, day: { glyph: '◕', label: 'a whole day' } } as const;
const BUDGET = { free: { glyph: '◇', label: 'free' }, low: { glyph: '◈', label: 'cheap' }, medium: { glyph: '◆', label: 'moderate' }, high: { glyph: '◆◆', label: 'splurge' } } as const;
const PREP = { none: { glyph: '▢', label: 'zero prep' }, some: { glyph: '▣', label: 'some prep' }, plan_ahead: { glyph: '▦', label: 'plan ahead' } } as const;

/** The shape of the evening, in words. Sentence case: this is a caption, not a column head. */
const iconWords = (c: Pick<DateCard, 'timeIcon' | 'budgetIcon' | 'prepIcon'>) => {
  const line = `${TIME[c.timeIcon].label} · ${BUDGET[c.budgetIcon].label} · ${PREP[c.prepIcon].label}`;
  return line.charAt(0).toUpperCase() + line.slice(1);
};

/** The three marks in a row, for the tile face. */
const iconGlyphs = (c: Pick<DateCard, 'timeIcon' | 'budgetIcon' | 'prepIcon'>) =>
  `${TIME[c.timeIcon].glyph} ${BUDGET[c.budgetIcon].glyph} ${PREP[c.prepIcon].glyph}`;

/**
 * THE THREE FILTERS OVER THE WALL — the questions somebody actually arrives with.
 *
 * Not a search and not a sort: a wall of thirty sealed cards is only navigable by the one thing
 * the client is allowed to know about them, which is the icons. "Tonight" is anything that is not
 * a whole day; the other two are exactly their icon. They compose, and a combination with nothing
 * in it says so rather than showing an empty wall.
 */
const FILTERS = [
  { key: 'tonight', label: 'Tonight', match: (c: DateCard) => c.timeIcon !== 'day' },
  { key: 'free', label: 'Free', match: (c: DateCard) => c.budgetIcon === 'free' },
  { key: 'zeroprep', label: 'Zero prep', match: (c: DateCard) => c.prepIcon === 'none' },
] as const;
type FilterKey = (typeof FILTERS)[number]['key'];

/** The tile a photo-less entry gets, so the mosaic still has a shape in that square. */
const tileInitial = (title: string) => (title.trim()[0] ?? '·').toUpperCase();

/** The gutter between tiles, in px. Applied as tile padding against a negative container
 *  margin — percentage widths and `gap` cannot both be honoured without measuring the container. */
const TILE_GAP = 8;

/**
 * How many tracks the wall has, off `useLayout` and nothing else.
 *
 * The one place in this file that asks about width, and it asks in the design system's own two
 * questions rather than in pixels — a screen that compares against a number has taken a dependency
 * on a decision the design file owns.
 */
function useColumns(): number {
  const { wide, expanded } = useLayout();
  return expanded ? 6 : wide ? 4 : 3;
}

/** The next fourteen days, as local `YYYY-MM-DD`, for the "Pick a day" wheel. */
function nextDays(count: number): string[] {
  const out: string[] = [];
  const start = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    out.push(localDay(d.toISOString()));
  }
  return out;
}

/** The coming Saturday — today if today IS Saturday, so "this weekend" is never last weekend. */
function comingSaturday(): string {
  const now = new Date();
  const ahead = (6 - now.getDay() + 7) % 7;
  return localDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + ahead).toISOString());
}

export function DatesScreen() {
  const { me, refreshBadges, fail, selected, select, look } = useStore();

  /**
   * The edge a committed card wears.
   *
   * Tuxedo spends its one red on exactly three objects — the primary button, the dock badge, the
   * meter fill — and every other emphasis in that room is gold. A `border-primary` here is a
   * fourth red object, which is the thing the look is defined by NOT having, so the edge follows
   * the look rather than being decided once for both.
   */
  const emphasis = look === 'tuxedo' ? 'border-gold' : 'border-primary';
  const cols = useColumns();
  const reduced = useReducedMotion();

  const [cards, setCards] = React.useState<DateCard[]>([]);
  const [scrapbook, setScrapbook] = React.useState<ScrapbookEntry[]>([]);
  const [mode, setMode] = React.useState<WallMode | null>(null);
  /** What the wall opened on, decided once. See the note where it is pinned. */
  const defaultWall = React.useRef<WallMode | null>(null);
  const [filters, setFilters] = React.useState<FilterKey[]>([]);
  const [confirming, setConfirming] = React.useState<string | null>(null);
  const [completing, setCompleting] = React.useState<string | null>(null);
  const [picking, setPicking] = React.useState<string | null>(null);
  const [pickDay, setPickDay] = React.useState<string>(() => nextDays(1)[0]!);
  const [note, setNote] = React.useState('');
  const [photo, setPhoto] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  /**
   * The photographs, one per entry, fetched once and kept for the life of the panel.
   *
   * The list no longer carries them — see `ScrapbookEntry`. This is the cache that makes the wall
   * a wall again: a square with `hasPhoto` asks for its bytes the first time it is drawn, and
   * never again, however many times the thirty-second poll re-lists it.
   */
  const [photos, setPhotos] = React.useState<Record<string, string>>({});
  const fetching = React.useRef(new Set<string>());

  /** The mosaic's selection, in the store rather than in this component, so a push can reach it. */
  const activeId = selected.dates ?? null;

  const body = React.useRef<ScrollView>(null);
  /** Where the inline confirm cell sits in the scroller, measured as it lays out. */
  const confirmY = React.useRef(0);

  const load = React.useCallback(async () => {
    const [c, s] = await Promise.all([
      api.get<DateCard[]>('/api/dates/cards'),
      api.get<ScrapbookEntry[]>('/api/dates/scrapbook'),
    ]);
    setCards(c);
    setScrapbook(s);
  }, []);

  const { loaded, error, reload } = usePoll(load, 30_000);

  /** Pull any photograph the wall is about to draw and does not have yet. Once each. */
  React.useEffect(() => {
    for (const entry of scrapbook) {
      if (!entry.hasPhoto || photos[entry.id] || fetching.current.has(entry.id)) continue;
      fetching.current.add(entry.id);
      void api
        .get<{ photo: string | null }>(`/api/dates/scrapbook/${entry.id}`)
        .then((full) => {
          if (full?.photo) setPhotos((p) => ({ ...p, [entry.id]: full.photo! }));
        })
        // Silent: a square that could not fetch its picture draws its initial, which is the same
        // thing a photo-less entry draws. A failed image is not worth an alert.
        .catch(() => fetching.current.delete(entry.id));
    }
  }, [scrapbook, photos]);

  /**
   * THE PANEL OPENS ON WHAT THEY HAVE, once they have anything.
   *
   * Sealed is the right first screen for a couple with an empty scrapbook and the wrong one for
   * everybody else: the shelf of what you did together is the reason to come back, and the wall of
   * question marks is one tap away either way.
   *
   * PINNED on the first loaded render rather than derived every time, and pinned in a ref rather
   * than in an effect: derived, the wall would flip out from under somebody the moment their
   * partner logged a date; in an effect, the first frame draws the wrong wall and then swaps. The
   * write is idempotent and happens once, which is the one case where writing a ref during render
   * is the simplest honest thing.
   */
  if (loaded && defaultWall.current === null) defaultWall.current = scrapbook.length ? 'done' : 'sealed';
  const wall: WallMode = mode ?? defaultWall.current ?? 'sealed';

  const sealed = cards.filter((c) => c.status === 'sealed');
  const scratched = cards.filter((c) => c.status === 'scratched');
  const shown = filters.length
    ? sealed.filter((c) => filters.every((k) => FILTERS.find((f) => f.key === k)!.match(c)))
    : sealed;

  const dates = scrapbook.filter((e) => e.type === 'date');
  const doneDates = dates.length;

  /**
   * How long since the last date, in whole days.
   *
   * The scrapbook arrives newest first, so the first date entry is the most recent one. `null`
   * when there has never been one, which is a different sentence rather than a zero.
   */
  const lastDateDays = doneDates
    ? Math.max(0, Math.floor((Date.now() - Date.parse(dates[0]!.completedAt)) / 86_400_000))
    : null;
  const agoLabel =
    lastDateDays === null ? '' : lastDateDays === 0 ? 'today' : lastDateDays === 1 ? 'yesterday' : `${lastDateDays} days ago`;

  /** Downscaled and re-encoded by the picker, so a photo stays well inside the D1 row budget. */
  const pickPhoto = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.7,
      base64: true,
    }).catch(() => null);
    const asset = result && !result.canceled ? result.assets[0] : null;
    if (asset?.base64) setPhoto(`data:image/jpeg;base64,${asset.base64}`);
  };

  const complete = async (card: DateCard) => {
    setBusy(true);
    try {
      await api.post(`/api/dates/cards/${card.id}/complete`, {
        note: note.trim() || undefined,
        photo: photo ?? undefined,
      });
      setNote('');
      setPhoto(null);
      setCompleting(null);
      setMode('done');
      await Promise.all([load(), refreshBadges()]);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const plan = async (card: DateCard, day: string | null) => {
    try {
      await api.post(`/api/dates/cards/${card.id}/plan`, { plannedFor: day });
      setPicking(null);
      await Promise.all([load(), refreshBadges()]);
    } catch (e) {
      fail(e);
    }
  };

  const active = scrapbook.find((e) => e.id === activeId) ?? null;

  if (!loaded)
    return (
      <Panel id="dates">
        <PanelSkeleton rows={3} />
      </Panel>
    );
  if (error)
    return (
      <Panel id="dates">
        <LoadFailed onRetry={reload} reason={error} />
      </Panel>
    );

  const today = nextDays(1)[0]!;

  return (
    <Panel id="dates" bodyRef={body}>
      {/**
       * A sentence above the list, then the list's own switch.
       *
       * There was a coverage ring here, reading "dates done, out of dates done plus cards still
       * sealed". Its denominator moved every time the bank was refilled, so doing a date and
       * printing three new cards ran the ring BACKWARDS. What the two of them actually want to
       * know is how many dates they have had and how long since the last one, and that is a
       * sentence, not a fraction — set in sentence case, with the wall's own count as the quiet
       * footnote it is rather than as the louder of the two lines.
       */}
      <Card>
        <CardContent className="gap-2">
          {/* Capped: a two-option control stretched to 550px a segment is a pair of banners. */}
          <Segmented
            options={WALL_OPTIONS}
            value={wall}
            onChange={setMode}
            accessibilityLabel="Which wall"
            className="max-w-[320px]"
          />
          <Text>
            {doneDates ? `${doneDates} ${doneDates === 1 ? 'date' : 'dates'} done · last one ${agoLabel}` : 'No dates yet'}
          </Text>
          <Text variant="note">{cards.length} still on the wall</Text>
        </CardContent>
      </Card>

      {wall === 'done' ? (
        scrapbook.length ? (
          <>
            <Mosaic
              entries={scrapbook}
              photos={photos}
              cols={cols}
              activeId={activeId}
              onSelect={(id) => select('dates', id)}
            />

            {/**
             * The tapped tile's card, inline under the mosaic rather than replacing it. This wall's
             * whole claim is that a year of dates reads as one picture, and a detail view that
             * takes the picture off the screen to show one square of it undoes exactly that — so
             * the mosaic stays, the tile keeps a ring, and the card lands below it.
             */}
            {active ? (
              <Card className={emphasis}>
                <CardContent>
                  {/* The band is the card's first row, so the title leads and the photograph
                      lands under it — the picture is the entry's body, not its heading. */}
                  <CardHead row>
                    <Text variant="heading" className="min-w-0 flex-1">
                      {active.title}
                    </Text>
                    {active.type === 'wrap' ? (
                      <Badge variant="band">
                        <Text>Wrapped</Text>
                      </Badge>
                    ) : null}
                  </CardHead>
                  {photos[active.id] ? (
                    <Image source={{ uri: photos[active.id] }} className="h-52 w-full rounded-md" resizeMode="cover" />
                  ) : null}
                  {active.body ? <Text className="text-muted-foreground">{active.body}</Text> : null}
                  {active.note ? <Text className="italic">“{active.note}”</Text> : null}
                  <Text variant="small" className="text-muted-foreground">
                    {/* Sentence case and a day somebody would say out loud. It read
                        "you wrapped it · 2026-09-01" — a lowercase sentence and a database
                        column. `completedAt` is an instant, so it goes through `localDay` first. */}
                    {active.type === 'wrap'
                      ? `${whoLabel(active.author, me)} wrapped it`
                      : `${whoLabel(active.author, me)} logged it`}{' '}
                    · {dayLabel(localDay(active.completedAt))}
                  </Text>
                  <Button variant="ghost" size="sm" className="self-start" onPress={() => select('dates', null)}>
                    <Text>Close</Text>
                  </Button>
                </CardContent>
              </Card>
            ) : null}
          </>
        ) : (
          <Empty
            mark="◈"
            title="Nothing in the scrapbook yet"
            body="It starts with the first scratch — or the first wrap either of you keeps. Pick a card you both mean."
          />
        )
      ) : (
        <>
          {scratched.map((card) => (
            <Card key={card.id} className={emphasis}>
              <CardContent>
                {/* The commitment badge stays in the body rather than riding the band: it is a
                    sentence about who committed, and the band's second slot is for a mark.
                    GOLD, not `warning` — gold is what this app pays a reward in, and a night the
                    two of them committed to is not a thing that has gone wrong. */}
                <CardHead>{card.title ?? ''}</CardHead>
                <Badge variant="success">
                  <Text>Scratched by {whoLabel(card.scratchedBy!, me)} — committed</Text>
                </Badge>
                <Text>{card.body}</Text>
                <Text variant="small" className="text-muted-foreground">
                  {iconGlyphs(card)} · {iconWords(card)}
                </Text>

                {/**
                 * WHEN. A scratch is the commitment and it used to be dateless, which is how a wall
                 * of "we're in" becomes a wall of good intentions. Three chips, no calendar to
                 * open, and the third one is the wheel rather than a typed date.
                 *
                 * The day is joint and content-free — either of them may set it, neither is named
                 * on it — and what it buys is a nudge that says Dates on the day instead of "a few
                 * things are waiting".
                 */}
                {picking === card.id ? (
                  <WheelPicker
                    columns={[
                      {
                        label: 'Day',
                        value: pickDay,
                        values: nextDays(14),
                        onChange: setPickDay,
                        format: dayLabel,
                      },
                    ]}
                    readout={dayLabel(pickDay)}
                    onCancel={() => setPicking(null)}
                    onDone={() => void plan(card, pickDay)}
                  />
                ) : (
                  <View className="gap-1.5">
                    <Text variant="label">{card.plannedFor ? 'Planned for' : 'When?'}</Text>
                    <View className="flex-row flex-wrap gap-2">
                      {card.plannedFor ? (
                        <>
                          {/* Pressing the day OPENS the wheel. A selected chip that clears itself
                              is the right gesture for a filter and the wrong one for a date. */}
                          <Chip
                            label={dayLabel(card.plannedFor)}
                            selected
                            onPress={() => {
                              setPickDay(card.plannedFor!);
                              setPicking(card.id);
                            }}
                          />
                          <Button variant="ghost" size="sm" flush={false} onPress={() => void plan(card, null)}>
                            <Text>Clear</Text>
                          </Button>
                        </>
                      ) : (
                        <>
                          <Chip label="Tonight" onPress={() => void plan(card, today)} />
                          <Chip label="This weekend" onPress={() => void plan(card, comingSaturday())} />
                          <Chip
                            label="Pick a day"
                            onPress={() => {
                              setPickDay(card.plannedFor ?? today);
                              setPicking(card.id);
                            }}
                          />
                        </>
                      )}
                    </View>
                  </View>
                )}

                {completing === card.id ? (
                  <>
                    <Textarea value={note} onChangeText={setNote} placeholder="One line for the scrapbook…" />
                    {photo ? <Image source={{ uri: photo }} className="h-40 w-full rounded-md" resizeMode="cover" /> : null}
                    <View className="flex-row flex-wrap gap-2">
                      <Button variant="outline" onPress={pickPhoto}>
                        <Text>{photo ? 'Change photo' : 'Add a photo'}</Text>
                      </Button>
                      <Button disabled={busy} onPress={() => complete(card)}>
                        <Text>Into the scrapbook</Text>
                      </Button>
                      <Button variant="ghost" flush={false} onPress={() => setCompleting(null)}>
                        <Text>Cancel</Text>
                      </Button>
                    </View>
                  </>
                ) : (
                  <Button className="self-start" onPress={() => setCompleting(card.id)}>
                    <Text>We did it</Text>
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}

          {sealed.length ? (
            <>
              {/* Three questions somebody actually arrives with, over the wall they answer about.
                  They compose, and they are the only thing the client is allowed to know about a
                  sealed card — which is exactly why they are icons and not a search box. */}
              <View className="flex-row flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <Chip
                    key={f.key}
                    label={f.label}
                    selected={filters.includes(f.key)}
                    onPress={() =>
                      setFilters((cur) => (cur.includes(f.key) ? cur.filter((k) => k !== f.key) : [...cur, f.key]))
                    }
                  />
                ))}
              </View>

              {shown.length ? (
                <Wall
                  cards={shown}
                  cols={cols}
                  confirming={confirming}
                  emphasis={emphasis}
                  reduced={reduced}
                  onTap={(id) => setConfirming((cur) => (cur === id ? null : id))}
                  onConfirmLayout={(y) => {
                    confirmY.current = y;
                    body.current?.scrollTo({ y: Math.max(0, y - 24), animated: true });
                  }}
                  onScratch={async (card) => {
                    // POST first, THEN the flight — the tile turning over IS the face arriving, so
                    // it must not start before the server has agreed there is one. Same order the
                    // face-down card's lift uses, and the same duration.
                    await api.post(`/api/dates/cards/${card.id}/scratch`);
                  }}
                  onScratched={async () => {
                    setConfirming(null);
                    await Promise.all([load(), refreshBadges()]);
                    // The card lands at the top of the scratched list, which is above the wall.
                    body.current?.scrollTo({ y: 0, animated: true });
                  }}
                  onFail={fail}
                />
              ) : (
                <Empty
                  mark="◇"
                  title="Nothing matches"
                  body="Drop a filter — there are cards on the wall, just not that shape of evening."
                />
              )}
            </>
          ) : scratched.length ? null : (
            <Empty mark="◈" title="The card bank is empty" body="The AI is printing more — the engine refills it on its next tick." />
          )}
        </>
      )}
    </Panel>
  );
}

/**
 * THE SEALED WALL — fixed tracks, and the confirm card expanding in place.
 *
 * Tapping a tile used to render the confirm card at the TOP of the panel, above the scratched
 * cards: on a wall of thirty, tapping the twenty-fifth changed nothing anybody could see. It now
 * opens where the tile is — the tapped tile wears the emphasis ring, and the card takes a full-
 * width cell at the end of that tile's own row, so the wall opens rather than jumping.
 *
 * Confirming plays the reveal this app already has: the cell lifts, turns over, and the open card
 * is at the top of the list when it lands.
 */
function Wall({
  cards,
  cols,
  confirming,
  emphasis,
  reduced,
  onTap,
  onConfirmLayout,
  onScratch,
  onScratched,
  onFail,
}: {
  cards: DateCard[];
  cols: number;
  confirming: string | null;
  emphasis: string;
  reduced: boolean;
  onTap: (id: string) => void;
  onConfirmLayout: (y: number) => void;
  onScratch: (card: DateCard) => Promise<void>;
  onScratched: () => void | Promise<void>;
  onFail: (e: unknown) => void;
}) {
  const index = cards.findIndex((c) => c.id === confirming);
  /** The end of the tapped tile's row — where the expanded cell goes, so the grid stays a grid. */
  const insertAt = index >= 0 ? Math.min(cards.length, (Math.floor(index / cols) + 1) * cols) : -1;
  const card = index >= 0 ? cards[index]! : null;

  const cells: React.ReactNode[] = [];
  cards.forEach((c, i) => {
    cells.push(
      <View
        key={c.id}
        style={{ width: `${100 / cols}%`, paddingHorizontal: TILE_GAP / 2, paddingBottom: TILE_GAP }}>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: c.id === confirming }}
          accessibilityLabel={`Sealed card: ${iconWords(c)}`}
          onPress={() => onTap(c.id)}
          className={cn(
            /* `bg-card` with a real hairline, not `bg-muted`: in light, thirty muted tiles sat
               within a hair of the ground and read as ghosts of themselves. */
            'bg-card border-border h-[140px] items-center justify-center gap-1.5 rounded-lg border active:opacity-70',
            c.id === confirming && cn(emphasis, 'border-2'),
          )}>
          <Text variant="display" className="text-muted-foreground">
            ?
          </Text>
          <Text className="text-muted-foreground">{iconGlyphs(c)}</Text>
          {/* The shape of the evening is not the secret — only the card's face is. One line,
              sentence case: it was the same three facts a second time in ALL CAPS over three
              lines, which is a tile that shouts its own subtitle. */}
          <Text variant="note" className="px-1 text-center" numberOfLines={2}>
            {iconWords(c)}
          </Text>
        </Pressable>
      </View>,
    );
    if (card && i + 1 === insertAt) {
      cells.push(
        <ConfirmCell
          key={`confirm-${card.id}`}
          card={card}
          reduced={reduced}
          onLayout={onConfirmLayout}
          onScratch={onScratch}
          onScratched={onScratched}
          onCancel={() => onTap(card.id)}
          onFail={onFail}
        />,
      );
    }
  });

  return (
    <View className="flex-row flex-wrap" style={{ marginHorizontal: -TILE_GAP / 2 }}>
      {cells}
    </View>
  );
}

/**
 * The confirm card, as a full-width cell in the wall — and the lift that takes it away.
 *
 * Lift, turn, gone: `translateY` up while `rotateX` runs to 90° and the opacity goes with it, over
 * the same `FLIGHT_MS` the face-down card flies in. Under reduced motion the POST simply resolves
 * and the list refreshes, which is the same information without the movement.
 */
function ConfirmCell({
  card,
  reduced,
  onLayout,
  onScratch,
  onScratched,
  onCancel,
  onFail,
}: {
  card: DateCard;
  reduced: boolean;
  onLayout: (y: number) => void;
  onScratch: (card: DateCard) => Promise<void>;
  onScratched: () => void | Promise<void>;
  onCancel: () => void;
  onFail: (e: unknown) => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const lift = React.useRef(new Animated.Value(0)).current;

  const scratch = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onScratch(card);
      if (reduced) {
        await onScratched();
        return;
      }
      Animated.timing(lift, { toValue: 1, duration: FLIGHT_MS, useNativeDriver: false }).start(() => {
        void onScratched();
      });
    } catch (e) {
      onFail(e);
      setBusy(false);
    }
  };

  return (
    <Animated.View
      onLayout={(e) => onLayout(e.nativeEvent.layout.y)}
      style={{
        width: '100%',
        paddingHorizontal: TILE_GAP / 2,
        paddingBottom: TILE_GAP,
        opacity: lift.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }),
        transform: [
          { perspective: 800 },
          { translateY: lift.interpolate({ inputRange: [0, 1], outputRange: [0, -120] }) },
          { rotateX: lift.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '90deg'] }) },
        ],
      }}>
      <Card>
        <CardContent>
          <CardHead>Scratch this one?</CardHead>
          <Text>
            {iconGlyphs(card)} · {iconWords(card)}
          </Text>
          <CardDescription>
            Scratching is a commitment — whatever is under there, you are both doing it. No un-scratching.
          </CardDescription>
          <View className="flex-row flex-wrap gap-2">
            <Button disabled={busy} onPress={scratch}>
              <Text>Scratch it</Text>
            </Button>
            <Button variant="ghost" flush={false} disabled={busy} onPress={onCancel}>
              <Text>Keep it sealed</Text>
            </Button>
          </View>
        </CardContent>
      </Card>
    </Animated.View>
  );
}

/**
 * The scrapbook as a wall of squares, newest first — the server already sorts it that way, so the
 * order here is the record's own and not a second opinion about it.
 *
 * Three across on a phone, four or six where there is room: the number of columns is a width
 * decision like every other one, read from `useLayout` by the caller. Widths are percentages
 * against a negative container margin rather than a `gap`, because a gap and a percentage width
 * cannot both be honoured without measuring the container first — and a mosaic that has to
 * measure itself before it can draw is one that flashes on every load.
 *
 * The photographs come from the caller's cache rather than from the entry: the list carries none.
 * A square whose bytes have not landed yet draws its initial, which is what a photo-less entry
 * draws anyway, so the wall is never a grid of spinners.
 */
function Mosaic({
  entries,
  photos,
  cols,
  activeId,
  onSelect,
}: {
  entries: ScrapbookEntry[];
  photos: Record<string, string>;
  cols: number;
  activeId: string | null;
  onSelect: (id: string | null) => void;
}) {
  // Read here rather than passed down: the selection ring is this component's own emphasis, and a
  // prop threaded through for it would be one more thing a caller could forget. Tuxedo's red is
  // spent on three objects and a ring is not one of them, so the picked tile wears gold there.
  const look = useStore((s) => s.look);
  const emphasis = look === 'tuxedo' ? 'border-gold' : 'border-primary';

  return (
    <View className="flex-row flex-wrap" style={{ marginHorizontal: -TILE_GAP / 2 }}>
      {entries.map((entry) => {
        const selected = entry.id === activeId;
        const photo = photos[entry.id];
        return (
          <View
            key={entry.id}
            style={{ width: `${100 / cols}%`, paddingHorizontal: TILE_GAP / 2, paddingBottom: TILE_GAP }}>
            <Pressable
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={`${entry.title}${entry.hasPhoto ? '' : ' (no photo)'} — ${dayLabel(localDay(entry.completedAt))}`}
              onPress={() => onSelect(selected ? null : entry.id)}
              style={{ aspectRatio: 1 }}
              className={cn(
                'bg-muted border-border overflow-hidden rounded-lg border active:opacity-70',
                selected && cn(emphasis, 'border-2'),
              )}>
              {photo ? (
                <Image source={{ uri: photo }} className="h-full w-full" resizeMode="cover" />
              ) : (
                <View className="h-full w-full items-center justify-center">
                  <Text variant="heading" className="text-muted-foreground">
                    {tileInitial(entry.title)}
                  </Text>
                </View>
              )}

              {/**
               * The tick. Every square on this wall has happened — that is what puts it here — so
               * the badge is not a status the tile could be without. A wrap is marked apart rather
               * than ticked: it is something one of them kept, not a date the two of them did, and
               * one mark for both would make the wall claim more dates than there are.
               */}
              <View
                className={cn(
                  'absolute bottom-1 right-1 size-5 items-center justify-center rounded-sm',
                  entry.type === 'date' ? 'bg-primary' : 'bg-secondary',
                )}>
                <Text
                  variant="caption"
                  className={entry.type === 'date' ? 'text-primary-foreground' : 'text-secondary-foreground'}>
                  {entry.type === 'date' ? '✓' : '◈'}
                </Text>
              </View>
            </Pressable>
          </View>
        );
      })}
    </View>
  );
}
